// Real bearer-JWT auth context for Phase 2C live mode.
// Wraps token storage + user lookup, exposes useAuth().
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { tokenStore, setOnUnauthorized, login as apiLogin } from "../api/platformClient";

const REQUIRED_ROLE = "platform_superadmin";

const AuthContext = createContext(null);

const userIsSuperadmin = (u) => {
  if (!u) return false;
  if (typeof u.role === "string" && u.role === REQUIRED_ROLE) return true;
  if (typeof u.platform_role === "string" && u.platform_role === REQUIRED_ROLE) return true;
  if (Array.isArray(u.roles) && u.roles.includes(REQUIRED_ROLE)) return true;
  return false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [token, setToken] = useState(() => tokenStore.get());
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);

  // When the platformClient sees a 401 it calls this to clear local state.
  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(null);
      setToken(null);
    });
    return () => setOnUnauthorized(null);
  }, []);

  const doLogin = useCallback(async ({ email, password }) => {
    setLoggingIn(true);
    setLoginError(null);
    try {
      const { token: t, user: u } = await apiLogin({ email, password });
      tokenStore.set(t);
      tokenStore.setUser(u);
      setToken(t);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      setLoginError(e.message || "Login failed");
      return { ok: false, error: e };
    } finally {
      setLoggingIn(false);
    }
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!token,
      isSuperadmin: userIsSuperadmin(user),
      requiredRole: REQUIRED_ROLE,
      login: doLogin,
      logout,
      loginError,
      loggingIn,
    }),
    [user, token, doLogin, logout, loginError, loggingIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      user: null,
      token: null,
      isAuthenticated: false,
      isSuperadmin: false,
      requiredRole: REQUIRED_ROLE,
      login: async () => ({ ok: false }),
      logout: () => {},
      loginError: null,
      loggingIn: false,
    };
  }
  return ctx;
};
